import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { inject, observer } from 'mobx-react'
import { extendObservable } from 'mobx'
import moment from 'moment'
import PropTypes from 'prop-types'
import DocumentTitle from 'react-document-title'
import map from 'lodash/map'

import { hfComplianceApi } from '@atheneum/api'
import { formatDateTime } from '@atheneum/api/dist/lib'

import './HFCompliance.sass'
import Action from '@components/Action'
import Button from '@components/Button'
import Comments from '@components/Comments'
import { showDialog, isCancellationError } from '@components/Dialog'
import ArrowBackIcon from '@components/Icon/ArrowBack'
import Spinner from '@components/spinner'
import Error from '@components/Error/NoContent'

class HFCompliance extends Component {
  static propTypes = {
    staticData: PropTypes.object,
    userStore: PropTypes.object,
    expertStore: PropTypes.object,
    growlStore: PropTypes.object,
    modalStore: PropTypes.object,
    match: PropTypes.shape({
      params: PropTypes.shape({
        hfComplianceId: PropTypes.string
      }).isRequired,
      url: PropTypes.string
    }).isRequired
  }

  constructor (props) {
    super(props)
    extendObservable(this, {
      loading: true,
      hfCompliances: [],
      error: null,
      hasExpired: null
    })
    this.loadHfCompliance(props.match.params.hfComplianceId)
  }

  loadHfCompliance = id => {
    const { staticData, expertStore } = this.props
    this.loading = true
    hfComplianceApi
      .getHfCompliance(id, expertStore.id)
      .then(hfCompliance => {
        this.hfCompliance = hfCompliance

        this.hasExpired = moment(hfCompliance.createdAt).isBefore(
          moment().subtract(staticData.hfComplianceExpiryDays, 'days')
        )
        this.loading = false
      })
      .catch(err => {
        this.error = err
        this.loading = false
      })
  }

  generateBackLink = () => {
    const { match } = this.props
    return (
      match.url.substr(0, match.url.lastIndexOf('/hf-compliance')) +
      '/hf-compliances'
    )
  }

  // TODO: add colors, perhaps similar to payment details' statuses
  renderStatus ({ createdAt, isConfirmed }) {
    if (this.hasExpired) {
      return 'Expired'
    } else if (isConfirmed === false) {
      return 'Rejected'
    } else if (isConfirmed) {
      return 'Confirmed'
    } else {
      return 'Unconfirmed'
    }
  }

  renderUpdatedBy ({ employee }) {
    if (employee) {
      return employee.fullName
    } else {
      return '-'
    }
  }

  renderLabel (label) {
    const wrapper = `<strong>${label}</strong>`

    return <div dangerouslySetInnerHTML={{ __html: wrapper }} />
  }

  renderAnswer ({ answer, targetValue }) {
    let className = ''
    if (answer && answer !== 'No answer') {
      if (answer === targetValue) className = 'green-text'
      else className = 'red-text'

      return (
        <div className={`${className} m-b-md`}>
          <strong>{answer}</strong>
        </div>
      )
    } else {
      return <div className={`${className} m-b-md`}>{answer}</div>
    }
  }

  renderActions = () => {
    const { staticData, userStore, expertStore, growlStore } = this.props
    const { HF_MANAGEMENT } = staticData.CONST.PERMISSIONS
    const isPermitted =
      userStore.isAdmin() || userStore.hasPermission(HF_MANAGEMENT)
    if (!isPermitted || this.hasExpired) return null
    const button = action => (
      <Button
        primary
        onClick={() => {
          return showDialog({
            title: `${action} hedge fund compliance?`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: action
          })
            .then(() => {
              return hfComplianceApi[action.toLowerCase()](this.hfCompliance.id)
            })
            .then(res => {
              expertStore.get(expertStore.id)
            })
            .catch(err => {
              if (isCancellationError(err)) return
              growlStore.showError(err)
            })
        }}
      >
        {action}
      </Button>
    )
    return (
      <div className='m-t-lg'>
        {!this.hfCompliance.isConfirmed && button('Confirm')}
        {(this.hfCompliance.isConfirmed ||
          this.hfCompliance.isConfirmed === null) &&
          button('Reject')}
      </div>
    )
  }

  showPdfExportDialog = (hfCompliance, expertName) => {
    this.props.modalStore.show({
      type: 'HF_COMPLIANCE_PDF_EXPORT',
      props: {
        hfCompliance,
        expertName
      }
    })
  }

  render () {
    if (this.loading) return <Spinner />
    if (this.error) return <Error />
    const { expertStore } = this.props

    const isYesNoQuestion = item => ['Yes', 'No'].includes(item.targetValue)

    return (
      <DocumentTitle
        title={`${expertStore.expert.fullName} - Hedge fund compliance | Atheneum Partners`}
      >
        <div className='invoice-wrapper animate-intro'>
          <div className='actions-wrapper actions-wrapper__noTop actions-wrapper--space-between'>
            <ul className='actions left-list'>
              <Link to={this.generateBackLink()}>
                <Action>
                  <li>
                    <ArrowBackIcon />
                    <span>Expert hedge fund compliance list</span>
                  </li>
                </Action>
              </Link>
            </ul>
            <ul className='actions'>
              <li>
                <Action
                  onClick={() =>
                    this.showPdfExportDialog(
                      this.hfCompliance,
                      expertStore.expert.fullName
                    )
                  }
                >
                  Export
                </Action>
              </li>
            </ul>
          </div>
          <hr className='hr-separator hr-separator--light' />
          <div className='details-list'>
            <div>Submission date:</div>
            <div>{formatDateTime(this.hfCompliance.createdAt, false)}</div>
          </div>
          <div className='details-list'>
            <div>Status:</div>
            <div>{this.renderStatus(this.hfCompliance)}</div>
          </div>
          <div className='details-list'>
            <div>Updated by:</div>
            <div>{this.renderUpdatedBy(this.hfCompliance)}</div>
          </div>
          {this.renderActions()}
          {map(this.hfCompliance.hfComplianceItems, (item, index) => (
            <div key={index} className='hf-compliance-wrapper'>
              <hr className='hr-separator hr-separator--light' />
              <div
                className={`${
                  isYesNoQuestion(item) ? 'yes-no' : 'oneline-question-answer'
                }`}
              >
                {item.label && this.renderLabel(item.label)}&nbsp;
                {this.renderAnswer(item)}
              </div>
              {item.subquestions && (
                <div>
                  {item.subquestions.label && (
                    <div className='subquestion-groups-label'>
                      {this.renderLabel(item.subquestions.label)}
                    </div>
                  )}

                  <div className='subquestion-groups'>
                    {map(item.subquestions.answer, (subitem, subindex) => (
                      <div key={subindex} className='subquestion-group'>
                        {map(subitem, (subquestion, subquestionIndex) => (
                          <div
                            key={subquestionIndex}
                            className={
                              subquestion.type !== 'text_area'
                                ? 'oneline-question-answer'
                                : ''
                            }
                          >
                            {subquestion.label &&
                              this.renderLabel(subquestion.label)}
                            {subquestion.answer}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Comments modelId={item.id} model='hf_compliance_item' />
            </div>
          ))}
        </div>
      </DocumentTitle>
    )
  }
}

export default inject(
  'staticData',
  'userStore',
  'expertStore',
  'growlStore',
  'modalStore'
)(observer(HFCompliance))
